import React, { useState, useEffect } from "react";
import axios from "axios";
import { Save, Trash2, Plus, Edit, Eye, EyeOff, ArrowLeft, LogOut } from "lucide-react";
import { Link } from "react-router-dom";
import { AdminAuthProvider, AdminLogin, useAdminAuth } from "../components/AdminAuth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogAdminContent = () => {
  const { isAuthenticated, loading: authLoading, logout, getAuthHeader } = useAdminAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  // Prevent indexing of admin pages
  useEffect(() => {
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.name = 'robots';
      document.head.appendChild(metaRobots);
    }
    metaRobots.content = 'noindex, nofollow';
    document.title = 'Blog Admin | Internal';
    
    return () => {
      if (metaRobots) {
        metaRobots.content = 'index, follow';
      }
    };
  }, []);

  const emptyPost = {
    title: "",
    slug: "",
    author: "Ted Valdez",
    publish_date: new Date().toISOString().split("T")[0],
    meta_title: "",
    meta_description: "",
    excerpt: "",
    body: "",
    featured_image: "",
    published: false
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchPosts();
    }
  }, [isAuthenticated]);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog/posts?limit=100&include_drafts=true`, {
        headers: getAuthHeader()
      });
      setPosts(response.data);
    } catch (error) {
      console.error("Error fetching posts:", error);
      setMessage({ type: "error", text: "Failed to fetch posts" });
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    const currentPost = editingPost || emptyPost;
    const updates = { title };
    
    if (!currentPost.slug || currentPost.slug === generateSlug(currentPost.title)) {
      updates.slug = generateSlug(title);
    }
    
    if (!currentPost.meta_title) {
      updates.meta_title = `${title} | Onward Land Solutions`;
    }
    
    if (isCreating) {
      setEditingPost({ ...emptyPost, ...editingPost, ...updates });
    } else {
      setEditingPost({ ...editingPost, ...updates });
    }
  };

  const handleFieldChange = (field, value) => {
    if (isCreating && !editingPost) {
      setEditingPost({ ...emptyPost, [field]: value });
    } else {
      setEditingPost({ ...editingPost, [field]: value });
    }
  };

  const handleSave = async () => {
    const postData = editingPost || emptyPost;
    
    if (!postData.title || !postData.slug) {
      setMessage({ type: "error", text: "Title and slug are required" });
      return;
    }

    setSaving(true);
    setMessage({ type: "", text: "" });

    try {
      const payload = {
        title: postData.title,
        slug: postData.slug,
        author: postData.author || "Ted Valdez",
        publish_date: postData.publish_date,
        meta_title: postData.meta_title || `${postData.title} | Onward Land Solutions`,
        meta_description: postData.meta_description || postData.excerpt || "",
        excerpt: postData.excerpt || "",
        body: postData.body || "",
        featured_image: postData.featured_image || "",
        published: postData.published || false
      };

      if (isCreating) {
        await axios.post(`${API}/blog/posts`, payload, {
          headers: getAuthHeader()
        });
        setMessage({ type: "success", text: "Post created successfully" });
      } else {
        await axios.put(`${API}/blog/posts/${postData.id}`, payload, {
          headers: getAuthHeader()
        });
        setMessage({ type: "success", text: "Post updated successfully" });
      }

      await fetchPosts();
      setEditingPost(null);
      setIsCreating(false);
    } catch (error) {
      console.error("Error saving post:", error);
      if (error.response?.status === 401) {
        setMessage({ type: "error", text: "Session expired. Please login again." });
        logout();
      } else {
        setMessage({ 
          type: "error", 
          text: error.response?.data?.detail || "Failed to save post" 
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm("Are you sure you want to delete this post?")) {
      return;
    }

    try {
      await axios.delete(`${API}/blog/posts/${postId}`, {
        headers: getAuthHeader()
      });
      setMessage({ type: "success", text: "Post deleted successfully" });
      await fetchPosts();
      if (editingPost?.id === postId) {
        setEditingPost(null);
      }
    } catch (error) {
      console.error("Error deleting post:", error);
      if (error.response?.status === 401) {
        setMessage({ type: "error", text: "Session expired. Please login again." });
        logout();
      } else {
        setMessage({ type: "error", text: "Failed to delete post" });
      }
    }
  };

  const startCreate = () => {
    setIsCreating(true);
    setEditingPost({ ...emptyPost });
  };

  const startEdit = (post) => {
    setIsCreating(false);
    setEditingPost({ 
      ...post, 
      publish_date: post.publish_date ? post.publish_date.split("T")[0] : new Date().toISOString().split("T")[0]
    });
  };

  const cancelEdit = () => {
    setEditingPost(null);
    setIsCreating(false);
    setMessage({ type: "", text: "" });
  };

  // Show login if not authenticated
  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AdminLogin />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-white">Loading posts...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-slate-400 hover:text-white">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <h1 className="text-2xl font-bold">Blog Admin</h1>
          </div>
          <div className="flex items-center gap-4">
            {!editingPost && (
              <button
                onClick={startCreate}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded"
              >
                <Plus className="h-4 w-4" />
                New Post
              </button>
            )}
            <button
              onClick={logout}
              className="flex items-center gap-2 text-slate-400 hover:text-white px-3 py-2"
              title="Logout"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`mb-4 p-3 rounded ${
            message.type === "error" ? "bg-red-600/20 text-red-400 border border-red-600/50" : 
            "bg-green-600/20 text-green-400 border border-green-600/50"
          }`}>
            {message.text}
          </div>
        )}

        {/* Editor */}
        {(editingPost || isCreating) && (
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 mb-8">
            <h2 className="text-lg font-semibold mb-4">
              {isCreating ? "Create New Post" : "Edit Post"}
            </h2>

            <div className="grid gap-4">
              {/* Title */}
              <div>
                <label className="block text-sm text-slate-400 mb-1">Title *</label>
                <input
                  type="text"
                  value={editingPost?.title || ""}
                  onChange={handleTitleChange}
                  className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-2 text-white"
                  placeholder="Post title"
                />
              </div>

              {/* Slug */}
              <div>
                <label className="block text-sm text-slate-400 mb-1">Slug *</label>
                <input
                  type="text"
                  value={editingPost?.slug || ""}
                  onChange={(e) => handleFieldChange("slug", e.target.value)}
                  className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-2 text-white"
                  placeholder="post-url-slug"
                />
              </div>

              {/* Author & Date */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Author</label>
                  <input
                    type="text"
                    value={editingPost?.author || "Ted Valdez"}
                    onChange={(e) => handleFieldChange("author", e.target.value)}
                    className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Publish Date</label>
                  <input
                    type="date"
                    value={editingPost?.publish_date || ""}
                    onChange={(e) => handleFieldChange("publish_date", e.target.value)}
                    className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-2 text-white"
                  />
                </div>
              </div>

              {/* Meta Title */}
              <div>
                <label className="block text-sm text-slate-400 mb-1">Meta Title (SEO)</label>
                <input
                  type="text"
                  value={editingPost?.meta_title || ""}
                  onChange={(e) => handleFieldChange("meta_title", e.target.value)}
                  className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-2 text-white"
                  placeholder="Title for search engines"
                />
              </div>

              {/* Meta Description */}
              <div>
                <label className="block text-sm text-slate-400 mb-1">Meta Description (SEO)</label>
                <textarea
                  value={editingPost?.meta_description || ""}
                  onChange={(e) => handleFieldChange("meta_description", e.target.value)}
                  className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-2 text-white h-20"
                  placeholder="Description for search engines (150-160 chars)"
                />
              </div>

              {/* Featured Image */}
              <div>
                <label className="block text-sm text-slate-400 mb-1">Featured Image URL</label>
                <input
                  type="text"
                  value={editingPost?.featured_image || ""}
                  onChange={(e) => handleFieldChange("featured_image", e.target.value)}
                  className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-2 text-white"
                  placeholder="https://..."
                />
              </div>

              {/* Excerpt */}
              <div>
                <label className="block text-sm text-slate-400 mb-1">Excerpt</label>
                <textarea
                  value={editingPost?.excerpt || ""}
                  onChange={(e) => handleFieldChange("excerpt", e.target.value)}
                  className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-2 text-white h-20"
                  placeholder="Short summary for blog listing"
                />
              </div>

              {/* Body */}
              <div>
                <label className="block text-sm text-slate-400 mb-1">Body (HTML/Markdown)</label>
                <textarea
                  value={editingPost?.body || ""}
                  onChange={(e) => handleFieldChange("body", e.target.value)}
                  className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-2 text-white font-mono text-sm h-64"
                  placeholder="<p>Your blog content here...</p>"
                />
              </div>

              {/* Published Toggle */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleFieldChange("published", !editingPost?.published)}
                  className={`flex items-center gap-2 px-3 py-2 rounded ${
                    editingPost?.published 
                      ? "bg-green-600/20 text-green-400 border border-green-600/50" 
                      : "bg-slate-700 text-slate-400 border border-slate-600"
                  }`}
                >
                  {editingPost?.published ? (
                    <>
                      <Eye className="h-4 w-4" />
                      Published
                    </>
                  ) : (
                    <>
                      <EyeOff className="h-4 w-4" />
                      Draft
                    </>
                  )}
                </button>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-slate-700">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-600 px-4 py-2 rounded"
                >
                  <Save className="h-4 w-4" />
                  {saving ? "Saving..." : "Save Post"}
                </button>
                <button
                  onClick={cancelEdit}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Posts List */}
        <div className="bg-slate-800 border border-slate-700 rounded-lg">
          <div className="p-4 border-b border-slate-700">
            <h2 className="font-semibold">All Posts ({posts.length})</h2>
          </div>
          <div className="divide-y divide-slate-700">
            {posts.length === 0 ? (
              <div className="p-4 text-slate-400">No posts yet</div>
            ) : (
              posts.map((post) => (
                <div key={post.id} className="p-4 flex items-center justify-between hover:bg-slate-700/50">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium truncate">{post.title}</h3>
                      {post.published ? (
                        <span className="text-xs bg-green-600/20 text-green-400 px-2 py-0.5 rounded">
                          Published
                        </span>
                      ) : (
                        <span className="text-xs bg-slate-600/50 text-slate-400 px-2 py-0.5 rounded">
                          Draft
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-slate-400 mt-1">
                      /{post.slug} • {post.author} • {post.publish_date?.split("T")[0]}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <a
                      href={`/blog/${post.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-slate-400 hover:text-white"
                      title="View"
                    >
                      <Eye className="h-4 w-4" />
                    </a>
                    <button
                      onClick={() => startEdit(post)}
                      className="p-2 text-slate-400 hover:text-white"
                      title="Edit"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(post.id)}
                      className="p-2 text-slate-400 hover:text-red-400"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Wrap with Auth Provider
const BlogAdmin = () => {
  return (
    <AdminAuthProvider>
      <BlogAdminContent />
    </AdminAuthProvider>
  );
};

export default BlogAdmin;
